import { Component, effect, inject, input, InputSignal, Signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { CmsSections } from '@shared/cms/cms-sections';
import { NotFound } from '@features/not-found/not-found';
import { LandingSectionGroup, LandingSeo } from '@core/cms/landing.cms';
import { PageCmsService } from '@core/cms/page-cms.service';
import { SeoService } from '@core/seo/seo.service';

/**
 * Dynamic CMS page: loads an `article` by the `:slug` route parameter and renders its sections
 * through the shared {@link CmsSections} dispatcher — the same pipeline as the home page, just
 * sourced by slug instead of the landing single type. {@link PageCmsService} is provided here so
 * each routed page owns its own request.
 */
@Component({
    selector: 'app-dynamic-page',
    imports: [CmsSections, NotFound, TranslatePipe],
    providers: [PageCmsService],
    templateUrl: './dynamic-page.html',
    styleUrl: './dynamic-page.scss',
})
export class DynamicPage {
    private readonly _page: PageCmsService = inject(PageCmsService);
    private readonly _seo: SeoService = inject(SeoService);

    /** The `:slug` route parameter, bound via `withComponentInputBinding` (see `app.config.ts`). */
    public readonly slug: InputSignal<string> = input.required<string>();

    /** Page sections grouped by `blockId` for banded rendering. */
    protected readonly cmsGroups: Signal<ReadonlyArray<LandingSectionGroup>> =
        this._page.sectionGroups;

    /** Whether the CMS request is in flight. */
    protected readonly isLoading: Signal<boolean> = this._page.page.isLoading;

    /** `true` once the slug resolved to no matching page (a 404). */
    protected readonly notFound: Signal<boolean> = this._page.notFound;

    /** CMS `shared.seo` for the active language; re-runs when the slug or language changes. */
    private readonly _cmsSeo: Signal<LandingSeo | null> = this._page.seo;

    constructor() {
        // Feed the routed slug into the service so its httpResource fetches — and refetches when the
        // route param changes while this component is reused across dynamic pages.
        effect((): void => {
            this._page.slug.set(this.slug());
        });

        // Apply CMS SEO once it resolves (runs during SSR so the tags land in the server HTML),
        // otherwise fall back to the app-wide i18n/asset defaults. A missing page is deliberately
        // skipped: the `<app-not-found>` rendered in its place owns both the `noindex` tags and the
        // 404 response status, so touching SEO here would only race with it.
        effect((): void => {
            const seo: LandingSeo | null = this._cmsSeo();

            if (seo) {
                this._seo.applyCmsSeo(seo);

                return;
            }

            // While the request is in flight there is no CMS SEO *yet* — applying the defaults here
            // would overwrite the server-rendered tags (or the previous page's) for the length of
            // the fetch. See the same guard in `Home` for why the client refetches at all.
            if (this.isLoading() || this.notFound()) {
                return;
            }

            this._seo.applyDefaultSeo();
        });
    }
}
